import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { FaxDocViewer } from './FaxDocViewer';
import { ALL_PANELS, type FaxSummaryViewProps } from './type';

export const FaxSummaryView = (props: FaxSummaryViewProps) => {
  const { pdfUrl, InputColumn, OutputColumn } = props;

  // Start with Fax + Input view (activeIndex 0 shows panels 0 and 1)
  const [activeIndex, setActiveIndex] = useState(0);
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [highlightStyle, setHighlightStyle] = useState({ left: 0, width: 0 });

  const visibleIndices = [activeIndex, activeIndex + 1];

  const handleTabClick = (index: number) => {
    if (index >= ALL_PANELS.length - 1) {
      setActiveIndex(ALL_PANELS.length - 2);
    } else {
      setActiveIndex(index);
    }
  };

  useEffect(() => {
    const updateHighlight = () => {
      const container = tabContainerRef.current;
      const firstTab = tabRefs.current[activeIndex];
      const secondTab = tabRefs.current[activeIndex + 1];

      if (container && firstTab && secondTab) {
        const containerRect = container.getBoundingClientRect();
        const firstRect = firstTab.getBoundingClientRect();
        const secondRect = secondTab.getBoundingClientRect();

        setHighlightStyle({
          left: firstRect.left - containerRect.left,
          width: secondRect.right - firstRect.left,
        });
      }
    };

    updateHighlight();
    // Recalculate on window resize
    window.addEventListener('resize', updateHighlight);
    return () => window.removeEventListener('resize', updateHighlight);
  }, [activeIndex]);

  const Tabs = () => {
    return (
      <div className="flex justify-center py-3 border-b">
        <div
          ref={tabContainerRef}
          className="relative flex items-center gap-1 p-1.5 bg-muted rounded-xl"
        >
          {/* Sliding highlight indicator */}
          <div
            className="absolute top-1.5 bottom-1.5 bg-background rounded-lg shadow-sm transition-all duration-300 ease-out"
            style={{
              left: highlightStyle.left,
              width: highlightStyle.width,
            }}
          />

          {/* Tab buttons */}
          {ALL_PANELS.map((panel, index) => {
            const Icon = panel.icon;
            const isSelected = visibleIndices.includes(index);
            return (
              <button
                key={panel.id}
                ref={el => {
                  tabRefs.current[index] = el;
                }}
                onClick={() => handleTabClick(index)}
                className={cn(
                  'relative z-10 flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors',
                  isSelected ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
                title={panel.label}
              >
                <Icon className="h-4 w-4" />
                <span
                  className={cn(
                    'text-sm font-medium transition-all duration-200',
                    isSelected ? 'opacity-100' : 'opacity-70',
                  )}
                >
                  {panel.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {Tabs()}
      <div className="grid grid-cols-2 gap-4 p-4 flex-1 overflow-hidden">
        {activeIndex === 0 ? (
          <>
            {/* Fax + Input */}
            <div className="border rounded-lg overflow-hidden">
              <FaxDocViewer fileUrl={pdfUrl} />
            </div>
            <div className="border rounded-lg p-4 overflow-auto">{InputColumn}</div>
          </>
        ) : (
          <>
            {/* Input + Output */}
            <div className="border rounded-lg p-4 overflow-auto">{InputColumn}</div>
            <div className="border rounded-lg p-4 overflow-auto">{OutputColumn}</div>
          </>
        )}
      </div>
    </div>
  );
};
